import { QuestionOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  TimePicker,
  Tooltip,
  Upload,
  message,
  notification,
} from 'antd';

import AvatarComponent from '@/components/common/avatar';
import { capitalizeFirstLetter } from '@/helpers/dataHelper';
import classnames from 'classnames';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import {
  addRoomMeetings,
  clearRoomData,
  clearRoomError,
  deleteRoomMeetings,
  editRoomMeetings,
  getRoomMeetings,
  getRoomMeetingsLanding,
  respondGoingToMeeting,
  setRoomEditData,
} from '../../../../../actions/room';
import FormDataHelper from '../../../../../helpers/formDataHelper';
import LiveRoom from '../live';
import VideoModal from '../recorded-video/video-modal';
import useWidth from '@/helpers/widthUtil';

const RoomSchedule = ({ roomSlug, roomId, permission }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { isMobile, isTablet } = useWidth();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const { TextArea } = Input;

  const [visible, setVisible] = React.useState(false);
  const [editVisible, setEditVisible] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(1);
  const [roomDate, setRoomDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [file, setFile] = useState(null);
  const [allowRecord, setAllowRecord] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const [attendingVisible, setAttendingVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [query, setQuery] = useState({ page: 1, per_page: 10 });

  const meetings = useSelector((state: any) => state.roomReducer?.meetingList);
  const { addMeetingStatus, updateMeetingStatus } = meetings;
  const error = useSelector((state: any) => state.roomReducer?.error?.meeting);

  useEffect(() => {
    if (router?.query?.tab === 'live') {
      if (activeTab === 4) {
        dispatch(getRoomMeetingsLanding(roomSlug, {}));
      } else {
        dispatch(getRoomMeetingsLanding(roomSlug, { status: activeTab }));
      }
    }
  }, [activeTab, router?.query?.tab]);

  useEffect(() => {
    if (router?.query?.tab === 'live') {
      dispatch(getRoomMeetings(roomSlug, query));
    }
  }, [router?.query?.tab]);

  useEffect(() => {
    clearState();
  }, []);

  useEffect(() => {
    if (meetings?.success) {
      notification.success({ message: 'Meeting Successfully published' });
      setVisible(false);
      form.resetFields();
    }
  }, [meetings?.success]);

  useEffect(() => {
    if (addMeetingStatus === 'success') {
      notification.success({ message: 'Meeting Successfully published' });
      setVisible(false);
    }
  }, [addMeetingStatus]);

  useEffect(() => {
    if (updateMeetingStatus === 'success') {
      setEditVisible(false);
    }
  }, [updateMeetingStatus]);

  const liveMeetingCountdown = (meeting) => {
    var duration = moment.duration(meeting?.start_time, 'milliseconds');
    var interval = 1000;
    setInterval(function () {
      duration = moment.duration(
        duration.asMilliseconds() - interval,
        'milliseconds'
      );
    }, interval);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const clearState = () => {
    dispatch(clearRoomData());
    dispatch(
      setRoomEditData({
        name: 'meetings',
        key: 'success',
        value: false,
      })
    );
    dispatch(clearRoomError());
  };

  const range = (start, end) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  const disabledDate = (current) => {
    // Can not select days before today and today
    return current && current < moment().endOf('day');
  };

  const disabledDateTime = () => {
    return {
      disabledHours: () => range(0, 24).splice(4, 20),
      disabledMinutes: () => range(30, 60),
      disabledSeconds: () => [55, 56],
    };
  };

  const disabledRangeTime = (_, type) => {
    if (type === 'start') {
      return {
        disabledHours: () => range(0, 60).splice(4, 20),
        disabledMinutes: () => range(30, 60),
        disabledSeconds: () => [55, 56],
      };
    }
    return {
      disabledHours: () => range(0, 60).splice(20, 4),
      disabledMinutes: () => range(0, 31),
      disabledSeconds: () => [55, 56],
    };
  };

  const onFinish = async (values: any) => {
    values = {
      ...values,
      start_date: roomDate,
      start_time: startTime,
      end_time: endTime,
      allow_recording: allowRecord,
      room: roomId,
    };
    let value;
    if (file) {
      value = FormDataHelper(values, { thumbnail: file });
    } else {
      dispatch(addRoomMeetings(values));
      setFile(null);
      return;
    }
    dispatch(addRoomMeetings(value));
    setFile(null);
  };

  const normFile = (e: any) => {
    setFile(e.file.originFileObj);
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleEditClick = (e: any, each: any) => {
    var meetingData =
      meetings.data &&
      meetings.data.length !== 0 &&
      meetings.data.find((item: any) => item.id === each.id);
    setEditData(meetingData);
    setEditVisible(true);
    setRoomDate(meetingData?.start_date);
    setStartTime(meetingData?.start_time);
    setEndTime(meetingData?.end_time);
    setAllowRecord(meetingData?.allowRecord);
    editForm.setFieldsValue({
      topic: meetingData.topic,
      agenda: meetingData.agenda,
    });
    dispatch(clearRoomError());
  };

  const onEditFinish = (values) => {
    values = {
      ...values,
      start_date: roomDate,
      start_time: startTime,
      end_time: endTime,
      allow_recording: allowRecord,
    };
    let value;
    if (file) {
      value = FormDataHelper(values, { thumbnail: file });
    } else {
      dispatch(editRoomMeetings(values, editData?.slug));
      return;
    }
    dispatch(editRoomMeetings(value, editData?.slug));
  };

  const handleDelete = (e: any, slug: string) => {
    e.preventDefault();
    dispatch(deleteRoomMeetings(slug));
  };

  const handlePagination = (page, pageSize) => {
    setQuery({ ...query, page: page, per_page: pageSize });
  };

  const handleIntrested = (meeting) => {
    dispatch(respondGoingToMeeting({ meeting: meeting?.slug }));
  };

  const handleClassClick = (each) => {
    dispatch(editRoomMeetings({ status: 1 }, each?.slug));
  };

  const meetings1 = {
    loading: false,
    data: [
      {
        id: 5,
        created_at: '2022-03-29T14:15:13.915938+05:45',
        updated_at: '2022-03-29T14:15:13.916022+05:45',
        ip_address: null,
        is_active: true,
        is_kicked: false,
        suspend_status: null,
        status: 0,
        join_status: 0,
        created_by: null,
        updated_by: null,
        room: 4,
        user: {
          id: 2,
          first_name: 'Ganesh',
          last_name: 'Kuikel',
          email: 'ganesh@studyinfocentre.com',
          phone_number: null,
          avatar: null,
        },
      },
      {
        id: 4,
        created_at: '2022-03-29T13:57:38.710004+05:45',
        updated_at: '2022-03-29T13:57:38.710071+05:45',
        ip_address: null,
        is_active: true,
        is_kicked: false,
        suspend_status: null,
        status: null,
        join_status: 2,
        created_by: null,
        updated_by: null,
        room: 4,
        user: {
          id: 1,
          first_name: 'Anil',
          last_name: 'Aryal',
          email: 'admin@admin.com',
          phone_number: null,
          avatar: null,
        },
      },
    ],
  };

  return (
    <>
      <VideoModal
        videoVisible={videoVisible}
        closeModal={() => setVideoVisible(false)}
      />
      <Modal
        visible={attendingVisible}
        onCancel={() => setAttendingVisible(false)}
        footer={[]}
        title="Meeting Attendings"
        width={800}
      >
        <div className="table-responsive data-table">
          <table className="table">
            <thead>
              <tr>
                <th>Profile</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone No.</th>
              </tr>
            </thead>
            <tbody>
              {meetings1?.data?.length !== 0 &&
                meetings1?.data?.map((member, idx) => (
                  <tr
                    key={member?.id}
                    className={
                      !meetings1.loading &&
                      member?.suspend_status !== null &&
                      member?.suspend_status !== '' &&
                      'bg-danger-low-opacity'
                    }
                  >
                    <td>
                      {meetings1.loading ? (
                        <Skeleton.Avatar />
                      ) : (
                        <AvatarComponent
                          firstName={member?.user?.first_name}
                          lastName={member?.user?.last_name}
                          avatar={member?.user?.avatar}
                        />
                      )}
                    </td>

                    <td>
                      {meetings1.loading ? (
                        <Skeleton.Button
                          style={{ width: '100%' }}
                          active={true}
                          size="small"
                        />
                      ) : (
                        `${capitalizeFirstLetter(
                          member?.user?.first_name
                        )} ${capitalizeFirstLetter(member?.user?.last_name)}`
                      )}
                    </td>
                    {meetings1.loading ? (
                      <Skeleton.Button active={true} size="small" />
                    ) : (
                      <td>{member?.user?.email}</td>
                    )}
                    <td>
                      {meetings1.loading ? (
                        <Skeleton.Button active={true} size="small" />
                      ) : (
                        member?.user?.phone_number
                      )}{' '}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Modal>
      {/* Create a Meeting */}
      <Modal
        width={1000}
        className=""
        title={
          <>
            <h5 className="text-center m-0 p-0">Schedule A Meeting</h5>
            {error?.time_matched ||
              (error?.lecture && (
                <small className="text-danger text-center">
                  {error?.time_matched || error?.lecture}
                </small>
              ))}
          </>
        }
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <>
            <Button
              className="border-0 px-4 text-danger "
              key="cancel"
              onClick={() => setVisible(false)}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              className="px-5"
              style={{ borderRadius: '5px' }}
              onClick={() => {
                form
                  .validateFields()
                  .then((values) => {
                    onFinish(values);
                  })
                  .catch((err) => {
                    console.log('Validate Failed:', err);
                  });
              }}
              htmlType="submit"
              loading={meetings?.addMeetingStatus === 'request'}
            >
              Confirm
            </Button>
          </>,
        ]}
      >
        <Form form={form}>
          <div className="row">
            <div className="col-lg-6">
              <Form.Item
                name="topic"
                label="Topic"
                rules={[{ required: true, message: 'Please input meeting Topic!' }]}
              >
                <Input
                  placeholder="Enter Topic"
                  style={{
                    borderRadius: '5px',
                    fontSize: '14px',
                  }}
                />
              </Form.Item>
            </div>
            <div className="col-lg-6">
              <div className="row">
                {error?.time_matched && (
                  <small className="text-danger">{error?.time_matched}</small>
                )}
                <div className="col-lg-4">
                  <Form.Item label="Date">
                    <DatePicker
                      format="YYYY-MM-DD"
                      // disabledDate={disabledDate}
                      disabledTime={disabledDateTime}
                      onChange={(date, dateString) => setRoomDate(dateString)}
                      style={{
                        borderRadius: '5px',
                      }}
                    />
                    {error?.start_date && (
                      <small className="text-danger">{error?.start_date}</small>
                    )}
                  </Form.Item>
                </div>
                <div className="col-lg-4">
                  <Form.Item label="Start">
                    <TimePicker
                      onChange={(time, timeString) => setStartTime(timeString)}
                      style={{
                        borderRadius: '5px',
                      }}
                    />
                    {error?.start_time && (
                      <small className="text-danger">{error?.start_time}</small>
                    )}
                  </Form.Item>
                </div>
                <div className="col-lg-4">
                  <Form.Item label="End">
                    <TimePicker
                      onChange={(time, timeString) => setEndTime(timeString)}
                      style={{
                        borderRadius: '5px',
                      }}
                    />
                    {error?.end_time && (
                      <small className="text-danger">{error?.end_time}</small>
                    )}
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="row">
                <div className="col-lg-4">
                  <Form.Item label="Meeting Options" name="allow_recording">
                    <span className="d-block">
                      <Checkbox onChange={() => setAllowRecord(!allowRecord)}>
                        Allow Recording
                      </Checkbox>
                    </span>
                  </Form.Item>
                </div>
                <div className="col-lg-5">
                  <Form.Item label="Thumbnail">
                    <Form.Item
                      name="thumbnail"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        listType="picture"
                        className="upload-list-inline"
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined rev="" />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 pl-0">
            <Form.Item className="mb-0" label="Description(Optional)" name="agenda">
              <TextArea
                rows={3}
                placeholder="Enter Agenda of Meeting"
                className="py-2"
                style={{ borderRadius: '5px' }}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
      {/* Edit a Meeting */}
      <Modal
        key={0}
        width={1000}
        className=""
        title={
          <>
            <h5 className="text-center m-0 p-0">Update Meeting</h5>
            {error?.time_matched ||
              (error?.lecture && (
                <small className="text-danger text-center">
                  {error?.time_matched || error?.lecture}
                </small>
              ))}
          </>
        }
        visible={editVisible}
        onCancel={() => setEditVisible(false)}
        footer={[
          <>
            <Button
              className="border-0 px-4 text-danger "
              key="cancel"
              onClick={() => setEditVisible(false)}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              className="px-5"
              style={{ borderRadius: '5px' }}
              onClick={() => {
                editForm
                  .validateFields()
                  .then((values) => {
                    onEditFinish(values);
                  })
                  .catch((err) => {
                    console.log('Validate Failed:', err);
                  });
              }}
              htmlType="submit"
              loading={meetings?.updateMeetingStatus === 'request'}
            >
              Save
            </Button>
          </>,
        ]}
      >
        <Form form={editForm}>
          <div className="row">
            <div className="col-lg-4">
              <Form.Item name="topic" label="Topic">
                <Input
                  placeholder="Enter Topic"
                  style={{
                    borderRadius: '5px',
                    fontSize: '14px',
                  }}
                />
              </Form.Item>
            </div>
            <div className="col-lg-8">
              <div className="row">
                <div className="col-lg-4">
                  <Form.Item label="Date">
                    <DatePicker
                      format="YYYY-MM-DD"
                      disabledDate={disabledDate}
                      disabledTime={disabledDateTime}
                      onChange={(date, dateString) => setRoomDate(dateString)}
                      value={moment(roomDate)}
                      style={{
                        borderRadius: '5px',
                      }}
                    />
                    {error?.start_date && (
                      <small className="text-danger">{error?.start_date}</small>
                    )}
                  </Form.Item>
                </div>

                <div className="col-lg-4">
                  <Form.Item label="Start">
                    <TimePicker
                      value={moment(startTime, 'HH:mm')}
                      onChange={(time, timeString) => setStartTime(timeString)}
                      style={{
                        borderRadius: '5px',
                      }}
                    />
                    {error?.start_time && (
                      <small className="text-danger">{error?.start_time}</small>
                    )}
                  </Form.Item>
                </div>
                <div className="col-lg-4">
                  <Form.Item label="End">
                    <TimePicker
                      value={moment(endTime, 'HH:mm')}
                      onChange={(time, timeString) => setEndTime(timeString)}
                      style={{
                        borderRadius: '5px',
                      }}
                    />
                    {error?.end_time && (
                      <small className="text-danger">{error?.end_time}</small>
                    )}
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="row">
                <div className="col-lg-4">
                  <Form.Item label="Meeting Options" name="allow_recording">
                    <span className="d-block">
                      <Checkbox
                        checked={allowRecord}
                        onChange={() => setAllowRecord(!allowRecord)}
                      >
                        Allow Recording
                      </Checkbox>
                    </span>
                  </Form.Item>
                </div>
                <div className="col-lg-5">
                  <Form.Item label="Thumbnail">
                    <Form.Item
                      name="thumbnail"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        listType="picture"
                        className="upload-list-inline"
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined rev="" />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 pl-0">
            <Form.Item className="mb-0" label="Agenda(Optional)" name="agenda">
              <TextArea
                rows={3}
                placeholder="Enter Agenda of Meeting"
                className="py-2"
                style={{
                  borderRadius: '5px',
                }}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
      <div className={`container ${(isMobile || isTablet) && 'px-0'}`}>
        {permission && (
          <div className="d-flex justify-content-end ">
            <Button
              type="primary"
              className="rounded-lg"
              onClick={() => setVisible(true)}
            >
              New Schedule
              <span className="add-btn ml-2">
                <i className="fas fa-plus"></i>
              </span>
            </Button>
          </div>
        )}
        <div className="d-flex flex-wrap align-items-center justify-content-between mt-2">
          <Nav className="flex-column">
            <NavItem>
              <NavLink
                className={`${
                  isMobile ? 'px-1 mr-1 font-size-sm' : ''
                }  ${classnames({
                  active: activeTab === 1,
                })}`}
                onClick={() => {
                  toggle(1);
                }}
              >
                OnGoing
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`${
                  isMobile ? 'px-1 mr-1 font-size-sm' : ''
                }  ${classnames({
                  active: activeTab === 0,
                })}`}
                onClick={() => {
                  toggle(0);
                }}
              >
                Upcoming
              </NavLink>
            </NavItem>
            {permission && (
              <>
                <NavItem>
                  <NavLink
                    className={`${
                      isMobile ? 'px-1 mr-1 font-size-sm' : ''
                    }  ${classnames({
                      active: activeTab === 2,
                    })}`}
                    onClick={() => {
                      toggle(2);
                    }}
                  >
                    Previous
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`${
                      isMobile ? 'px-1 mr-1 font-size-sm' : ''
                    }  ${classnames({
                      active: activeTab === 3,
                    })}`}
                    onClick={() => {
                      toggle(3);
                    }}
                  >
                    Archived
                  </NavLink>
                </NavItem>
              </>
            )}
            <NavItem>
              <NavLink
                className={`${
                  isMobile ? 'px-1 mr-1 font-size-sm' : ''
                }  ${classnames({
                  active: activeTab === 4,
                })}`}
                onClick={() => {
                  toggle(4);
                }}
              >
                Recordings
              </NavLink>
            </NavItem>
          </Nav>
        </div>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={1} className="ongoing-tab">
            <LiveRoom />
            {/* <Empty description="No Class are live for now." /> */}
          </TabPane>
          <TabPane tabId={0} className="upcoming-tab">
            <Skeleton loading={meetings.loading}>
              <div className="schedule-content">
                <div className="row">
                  {meetings?.data?.length !== 0 ? (
                    meetings?.data?.map((meeting, idx) => (
                      <div
                        className={`col-lg-12 ${isMobile ? 'mt-3' : 'mb-3'}`}
                        key={idx}
                      >
                        <div className="card card-block p-3 card-stretch">
                          <div className="card-body">
                            <div className="d-flex flex-wrap align-items-center justify-content-between">
                              <div className="d-flex flex-wrap align-items-center">
                                <div className="date mr-3">
                                  <h6 className="text-danger">
                                    {moment(meeting.start_time, 'HH:mm').format(
                                      'LT'
                                    )}{' '}
                                    -{' '}
                                    {moment(meeting.end_time, 'HH:mm:ss').format(
                                      'LT'
                                    )}
                                  </h6>
                                </div>
                                <div className="border-left-event pl-3">
                                  <div className="media align-items-top">
                                    <h5 className="mb-3">{meeting?.topic}</h5>
                                    <div className="badge badge-warning ml-3">
                                      Upcoming
                                    </div>
                                    {!permission && meeting?.meeting_attending && (
                                      <div className="badge badge-success ml-3">
                                        Going
                                      </div>
                                    )}
                                  </div>
                                  <div className="media align-items-center">
                                    {/* <p className="mb-0 pr-3"><i className="las la-clock mr-2 text-info"></i>Starts {moment(meeting?.start_date).fromNow()}</p> */}
                                    <p className="mb-0">
                                      <i className="las la-map-marker mr-2 text-info"></i>
                                      Date:{' '}
                                      {moment(meeting?.start_date).format(
                                        'D MMMM YYYY'
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              {permission && (
                                <div className="d-flex align-items-center list-action mt-lg-0 mt-2">
                                  <a
                                    onClick={() => setAttendingVisible(true)}
                                    className="badge mr-3"
                                    title="Attendings"
                                  >
                                    <i className="fas fa-users"></i>
                                  </a>
                                  <a
                                    title="Copy to clipboard"
                                    className="badge mr-3"
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    data-original-title="Copy"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        `${window.location.origin}/room/live/${meeting.slug}`
                                      );
                                      message.success('Link Copied');
                                    }}
                                  >
                                    <i className="fas fa-copy"></i>
                                  </a>
                                  <a
                                    title="Edit Meeting"
                                    onClick={(e) => handleEditClick(e, meeting)}
                                    className="badge mr-3"
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    data-original-title="Edit"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </a>
                                  <Popconfirm
                                    title="Sure to delete?"
                                    onConfirm={(e) => handleDelete(e, meeting?.slug)}
                                  >
                                    <a
                                      className="badge"
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      title=""
                                      data-original-title="Delete"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </a>
                                  </Popconfirm>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="float-right">
                            {!permission ? (
                              <button
                                disabled={meeting?.able_to_join ? false : true}
                                className="btn btn-outline-danger rounded"
                              >
                                {'Join'}
                              </button>
                            ) : (
                              <button
                                className="btn btn-outline-danger rounded"
                                onClick={() => handleClassClick(meeting)}
                              >
                                {'Start'}
                              </button>
                            )}
                            {!permission && !meeting?.meeting_attending && (
                              <>
                                <button
                                  className="btn btn-outline-danger rounded ml-2"
                                  disabled={
                                    meetings?.respondGoingToMeetingStatus ===
                                    'request'
                                      ? true
                                      : false
                                  }
                                  onClick={() => handleIntrested(meeting)}
                                >
                                  Interested
                                </button>
                                <Tooltip title="This action can only been seen by Instructor.">
                                  <QuestionOutlined
                                    rev=""
                                    className="p-1 ml-2 text-dark cursor-pointer"
                                    style={{
                                      fontSize: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: '#f0f2f5',
                                    }}
                                  />
                                </Tooltip>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <Empty description="No meeting found" />
                  )}
                </div>
              </div>
            </Skeleton>
          </TabPane>
          {permission && (
            <>
              <TabPane tabId={2} className="previous-tab">
                <Skeleton loading={meetings.loading}>
                  <div className="schedule-content">
                    <div className="row">
                      {meetings?.data?.length !== 0 ? (
                        meetings?.data?.map((meeting, idx) => (
                          <div
                            className={`col-lg-12 ${isMobile ? 'mt-3' : 'mb-3'}`}
                            key={idx}
                          >
                            <div className="card card-block p-3 card-stretch">
                              <h5 className="text-muted">{meeting?.topic}</h5>
                              <div className="card-body">
                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                  <div className="d-flex flex-wrap align-items-center">
                                    <div className="date mr-3">
                                      <h6 className="text-danger">
                                        {moment(meeting.start_time, 'HH:mm').format(
                                          'LT'
                                        )}{' '}
                                        -{' '}
                                        {moment(meeting.end_time, 'HH:mm:ss').format(
                                          'LT'
                                        )}
                                      </h6>
                                    </div>
                                    <div className="border-left-event pl-3">
                                      <div className="media align-items-center">
                                        {/* <p className="mb-0 pr-3"><i className="las la-clock mr-2 text-info"></i>Starts {moment(meeting?.start_date).fromNow()}</p> */}
                                        <p className="mb-0">
                                          <i className="las la-map-marker mr-2 text-info"></i>
                                          Date:{' '}
                                          {moment(meeting?.start_date).format(
                                            'D MMMM YYYY'
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  {permission && (
                                    <div className="d-flex align-items-center list-action mt-lg-0 mt-2">
                                      <a
                                        onClick={() => setAttendingVisible(true)}
                                        className="badge mr-3"
                                        title="Attendings"
                                      >
                                        <i className="fas fa-users"></i>
                                      </a>
                                      <a
                                        title="Copy to clipboard"
                                        className="badge mr-3"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        data-original-title="Copy"
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            `${window.location.origin}/room/live/${meeting.slug}`
                                          );
                                          message.success('Link Copied');
                                        }}
                                      >
                                        <i className="fas fa-copy"></i>
                                      </a>
                                      <a
                                        title="Edit Meeting"
                                        onClick={(e) => handleEditClick(e, meeting)}
                                        className="badge mr-3"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        data-original-title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="float-right">
                                {!permission ? (
                                  <Button
                                    disabled={meeting?.able_to_join ? false : true}
                                    className="btn-secondary ml-auto border-danger"
                                  >
                                    {'Join'}
                                  </Button>
                                ) : (
                                  <Button
                                    className="btn-secondary ml-auto border-danger"
                                    onClick={() => handleClassClick(meeting)}
                                  >
                                    {'Start'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <Empty description="No meeting found" />
                      )}
                    </div>
                  </div>
                </Skeleton>
              </TabPane>
              <TabPane tabId={3} className="archived-tab">
                <Skeleton loading={meetings.loading}>
                  <div className="schedule-content">
                    <div className="row">
                      {meetings?.data?.length !== 0 ? (
                        meetings?.data?.map((meeting, idx) => (
                          <div
                            className={`col-lg-12 ${isMobile ? 'mt-3' : 'mb-3'}`}
                            key={idx}
                          >
                            <div className="card card-block p-3 card-stretch">
                              <h5 className="text-muted">{meeting?.topic}</h5>
                              <div className="card-body">
                                <div className="d-flex flex-wrap align-items-center justify-content-between">
                                  <div className="d-flex flex-wrap align-items-center">
                                    <div className="date mr-3">
                                      <h6 className="text-danger">
                                        {moment(meeting.start_time, 'HH:mm').format(
                                          'LT'
                                        )}{' '}
                                        -{' '}
                                        {moment(meeting.end_time, 'HH:mm:ss').format(
                                          'LT'
                                        )}
                                      </h6>
                                    </div>
                                    <div className="border-left-event pl-3">
                                      <div className="media align-items-center">
                                        <p className="mb-0">
                                          <i className="las la-map-marker mr-2 text-info"></i>
                                          Date:{' '}
                                          {moment(meeting?.start_date).format(
                                            'D MMMM YYYY'
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <Empty description="No meeting found" />
                      )}
                    </div>
                  </div>
                </Skeleton>
              </TabPane>
            </>
          )}
          <TabPane tabId={4} className="recording-tab">
            {/* <Card className='col-lg-12'>
                        <CardBody>
                        <strong>01: 00 AM - 02: 00 AM</strong>
                        <span className='d-flex m-1'>
                        <strong className='d-block'>My Meeting</strong>
                        <span className='time ml-2'>Sep 2</span>
                        </span>
                        <hr />
                        <p>Lorem ipsum dolor sit amet consectetur adipisicing elit.</p>
                        </CardBody>
                    </Card> */}
            {/* <RecordedVideo dashboard={true} /> */}
            {/* <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body rounded event-detail event-detail-danger disabled">
                  <div className="d-flex align-items-top justify-content-between">
                    <div>
                      <h4 className="mb-2 mr-4">
                        Reminder of App Project And Design
                      </h4>
                      <p className="mb-2 text-danger font-weight-500 text-uppercase">
                        <i className="las la-user pr-2"></i>One On One
                      </p>
                      <p className="card-description">
                        Calendify always allows you to set the reminder to optimize
                        task management roles and jobs.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center pb-3 pr-3 pl-3">
                  <a className="badge badge-success mr-3 px-xl-3">50 Min</a>
                  <Button
                    className="btn-secondary ml-auto border-danger"
                    onClick={() => setVideoVisible(true)}
                  >
                    View
                  </Button>
                </div>
              </div>
            </div>
          </div> */}
            <Skeleton loading={meetings.loading}>
              <div className="schedule-content">
                <div className="row">
                  {meetings?.data?.length !== 0 ? (
                    meetings?.data?.map((meeting, idx) => (
                      <div
                        className={`col-lg-12 ${isMobile ? 'mt-3' : 'mb-3'}`}
                        key={idx}
                      >
                        <div className="card card-block p-3 card-stretch">
                          <h5 className="text-muted">{meeting?.topic}</h5>
                          <div className="card-body">
                            <div className="d-flex flex-wrap align-items-center justify-content-between">
                              <div className="d-flex flex-wrap align-items-center">
                                <div className="date mr-3">
                                  <h6 className="text-danger">
                                    {moment(meeting.start_time, 'HH:mm').format(
                                      'LT'
                                    )}{' '}
                                    -{' '}
                                    {moment(meeting.end_time, 'HH:mm:ss').format(
                                      'LT'
                                    )}
                                  </h6>
                                </div>
                                <div className="border-left-event pl-3">
                                  <div className="media align-items-center">
                                    {/* <p className="mb-0 pr-3"><i className="las la-clock mr-2 text-info"></i>Starts {moment(meeting?.start_date).fromNow()}</p> */}
                                    <p className="mb-0">
                                      <i className="las la-map-marker mr-2 text-info"></i>
                                      Date:{' '}
                                      {moment(meeting?.start_date).format(
                                        'D MMMM YYYY'
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <Empty description="No Recordings found" />
                  )}
                </div>
              </div>
              {/* {
                            meetings?.data?.length !== 0 &&
                            <Pagination
                            className='mt-5'
                            total={meetings?.meta?.total}
                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                            defaultPageSize={query?.per_page}
                            defaultCurrent={query?.page}
                            onChange={handlePagination}
                            showSizeChanger
                            />
                    } */}
            </Skeleton>
          </TabPane>
        </TabContent>
      </div>
    </>
  );
};

export default RoomSchedule;
